import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const links = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "/gallery" },
  { label: "Testimonials", href: "#testimonials" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    setOpen(false);

    // Route navigation
    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Anchor link
    if (location.pathname !== "/") {
      navigate("/" + href);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-28 md:h-32">
        <a
          href="#top"
          onClick={(e) => handleClick(e, "#top")}
          data-testid="nav-logo"
          className="group flex items-center gap-3"
          aria-label="Karnival Events & Entertainment"
        >
          <img
            src="/karnival-logo-transparent.png"
            alt="Karnival Events & Entertainment"
            className="h-20 md:h-24 w-auto object-contain group-hover:opacity-90 transition-opacity"
            loading="eager"
          />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={(e) => handleClick(e, l.href)}
              className="text-sm tracking-wide text-white/70 hover:text-[#FFFFFF] transition-colors duration-300 relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[1px] after:bg-[#FFFFFF] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="/contact"
          onClick={(e) => handleClick(e, "/contact")}
          data-testid="nav-cta-plan"
          className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFFFFF] text-black text-sm font-medium hover:bg-[#F5F5F5] transition-colors duration-300"
        >
          Contact Us
        </a>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col p-6 gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleClick(e, l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="text-lg text-white/80 hover:text-[#FFFFFF] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="/contact"
                onClick={(e) => handleClick(e, "/contact")}
                data-testid="nav-mobile-cta"
                className="mt-3 inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#FFFFFF] text-black font-medium"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
